interface InvoiceData {
  id: number;
  invoice_number: string;
  status: 'pending' | 'completed' | 'cancelled';
  type: 'order' | 'invoice';
  package_price?: number;
  discount_percentage?: number;
  discount_amount?: number;
  tva_amount?: number;
  total_to_pay?: number;
  amount: number | string;
  discount?: string;
  order_date: string;
  invoice_date?: string | null;
  payment_datetime?: string | null;
  starting_date?: string | null;
  ending_date?: string | null;
  payment_method?: string | null;
  payment_id?: string | null;
  package_id: number;
  package_name: string;
  package_period: string;
  client_details?: {
    name_complete: string;
    phone: string;
    email: string;
  };
  organisation_details?: {
    org_name: string;
    address: string;
    country: string;
    org_phone: string;
    zipcode: string;
    city: string;
    vat_number: string;
    company_size: string;
  };
  teamther_details?: {
    company_name: string;
    address: string;
    zipcode: string;
    city: string;
    country: string;
    vat_number: string;
    vat_percentage: number;
  };
  user_email?: string;
  user_name?: string;
  package_features?: Array<{
    key: string;
    title_en: string;
    title_fr: string;
    value: any;
    value_type: 'boolean' | 'integer' | 'text';
    is_static: boolean;
    display_order: number;
  }>;
}

// Translation texts
const translations = {
  en: {
    taxInvoice: 'Tax Invoice',
    order: 'Order',
    billTo: 'Bill to:',
    phone: 'Phone',
    address: 'Address',
    paid: 'Paid',
    pending: 'Pending',
    issued: 'Issued',
    items: 'Items',
    priceUsd: 'Price (EUR)',
    qty: 'Qty',
    total: 'Total',
    monthTerm: 'month term',
    monthsTerm: 'months term',
    discountApplied: 'Discount applied',
    subtotal: 'Subtotal',
    vat: 'VAT',
    from: 'From',
  },
  fr: {
    taxInvoice: 'Facture',
    order: 'Commande',
    billTo: 'Facturé à:',
    phone: 'Téléphone',
    address: 'Adresse',
    paid: 'Payé',
    pending: 'En attente',
    issued: 'Émis le',
    items: 'Articles',
    priceUsd: 'Prix (EUR)',
    qty: 'Qté',
    total: 'Total',
    monthTerm: 'mois',
    monthsTerm: 'mois',
    discountApplied: 'Réduction appliquée',
    subtotal: 'Sous-total',
    vat: 'TVA',
    from: 'De',
  },
};

export const generateInvoicePDF = async (invoice: InvoiceData, language: 'en' | 'fr' = 'en'): Promise<void> => {
  const t = translations[language];
  
  // Fetch app settings for invoice footer
  let invoiceFooter = '';
  try {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
    const response = await fetch(`${API_BASE_URL}/public/app-settings/`);
    if (response.ok) {
      const result = await response.json();
      const data = result.data || result;
      invoiceFooter = language === 'fr' 
        ? (data.contact_invoice_footer_fr || '') 
        : (data.contact_invoice_footer_en || '');
    }
  } catch (error) {
    console.error('Failed to load invoice footer:', error);
  }
  
  // Calculate amounts
  const packagePrice = Number(invoice.package_price || invoice.amount || 0);
  const discountAmount = Number(invoice.discount_amount || invoice.discount || 0);
  const discountPercent = Number(invoice.discount_percentage || (packagePrice > 0 ? (discountAmount / packagePrice * 100) : 0));
  const subtotal = packagePrice - discountAmount;
  const tvaAmount = Number(invoice.tva_amount || 0);
  const tvaPercent = Number(invoice.teamther_details?.vat_percentage || 0);
  const totalToPay = Number(invoice.total_to_pay || subtotal + tvaAmount);

  // Format date and time helper
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Calculate period duration
  const getPeriodText = () => {
    if (invoice.starting_date && invoice.ending_date) {
      const startDate = new Date(invoice.starting_date);
      const endDate = new Date(invoice.ending_date);
      const monthsDiff = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
      
      if (monthsDiff === 1) return `1-${t.monthTerm}`;
      return `${monthsDiff}-${t.monthsTerm}`;
    }
    return invoice.package_period;
  };

  // Create new window for PDF
  const pdfWindow = window.open('', '_blank');
  if (!pdfWindow) {
    alert('Please allow popups to view the invoice');
    return;
  }

  // Generate HTML content
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${invoice.type === 'invoice' ? t.taxInvoice : t.order} #${invoice.invoice_number}</title>
      <link rel="icon" type="image/png" href="https://app.teamther.ai/favicon.ico" />
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: Arial, sans-serif; 
          padding: 0; 
          background: #f5f5f5; 
          color: #000;
          line-height: 1.6;
        }
        .container { 
          max-width: 900px; 
          margin: 10px auto; 
          background: white;
          padding: 20px;
          border: 1px solid #ddd;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        /* Header */
        .header { 
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        .header-left {
          flex: 1;
          display: block;
        }
        .invoice-title { 
          font-size: 24px; 
          font-weight: 400;
          color: #000;
          margin-bottom: 0;
          font-family: Arial, sans-serif;
        }
        .company-logo {
          flex: 0 0 auto;
        }
        .company-logo img { 
          max-height: 120px;
          max-width: 300px;
          object-fit: contain;
        }
        
        /* Billing Section */
        .billing-section {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
          margin-top: 12px;
          gap: 30px;
        }
        .bill-to {
          flex: 1;
        }
        .bill-to.right {
          text-align: right;
        }
        .bill-to h3 {
          font-size: 13px;
          color: #000;
          margin-bottom: 6px;
          font-weight: 600;
          text-transform: none;
        }
        .bill-to-content {
          font-size: 13px;
          line-height: 1.5;
          color: #000;
        }
        .bill-to-name {
          font-weight: 700;
          margin-bottom: 5px;
          font-size: 15px;
          color: #000;
        }
        
        /* Order Status Section */
        .order-status-inline {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 8px;
        }
        .status-badge {
          display: inline-block;
          padding: 6px 16px;
          border-radius: 9999px;
          font-size: 13px;
          font-weight: 600;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .status-badge.pending {
          background-color: #fef3c7;
          color: #92400e;
        }
        .status-badge.completed {
          background-color: #d1fae5;
          color: #065f46;
        }
        .status-badge.cancelled {
          background-color: #fee2e2;
          color: #991b1b;
        }
        .status-date {
          font-size: 13px;
          color: #666;
          font-weight: 500;
        }
        
        /* Table */
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 10px;
        }
        table td {
          vertical-align: top;
        }
        thead {
          background: #17ad37;
          color: white;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        thead th {
          padding: 8px 12px;
          text-align: left;
          font-size: 13px;
          font-weight: 400;
          text-transform: none;
        }
        thead th:last-child {
          text-align: right;
        }
        tbody td {
          padding: 8px 12px;
          border-bottom: 1px solid #eee;
          font-size: 13px;
          color: #000;
        }
        tbody td:last-child {
          text-align: right;
        }
        .item-description {
          font-size: 10px;
          color: #666;
          margin-top: 2px;
        }
        
        /* Summary */
        .summary {
          margin-left: auto;
          width: 300px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          padding: 2px 0;
          font-size: 13px;
        }
        .summary-row.total {
          font-weight: bold;
          font-size: 15px;
          border-top: 2px solid #333;
          padding-top: 12px;
          margin-top: 8px;
        }
        
        /* Footer */
        .footer {
          margin-top: 8px;
          padding-top: 6px;
          border-top: 1px solid #ddd;
          text-align: center;
          font-size: 10px;
          color: #666;
        }
        
        @media print {
          body { padding: 5px; }
          .no-print { display: none; }
          .container { 
            margin: 5px auto;
            padding: 15px;
          }
          thead {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <!-- Header: Invoice Title + Status + Logo -->
        <div class="header">
          <div class="header-left">
            <div class="invoice-title">
              ${invoice.type === 'invoice' ? t.taxInvoice : t.order} #${invoice.invoice_number}
            </div>
            <div class="order-status-inline">
              ${invoice.status === 'pending' ? `
                <span class="status-badge pending">${language === 'fr' ? 'Paiement en attente' : 'Pending Payment'}</span>
                <span class="status-date">${language === 'fr' ? 'Date de commande' : 'Order Date'}: ${formatDate(invoice.order_date)}</span>
              ` : invoice.status === 'cancelled' ? `
                <span class="status-badge cancelled">${language === 'fr' ? 'Commande annulée' : 'Cancelled Order'}</span>
                <span class="status-date">${language === 'fr' ? 'Date de commande' : 'Order Date'}: ${formatDate(invoice.order_date)}</span>
              ` : invoice.type === 'invoice' && invoice.status === 'completed' ? `
                <span class="status-badge completed">${language === 'fr' ? 'Paiement effectué' : 'Payment Completed'}</span>
                <span class="status-date">${language === 'fr' ? 'Date de paiement' : 'Payment Date'}: ${formatDate(invoice.payment_datetime || invoice.invoice_date)}</span>
              ` : `
                <span class="status-date">${t.issued} ${formatDate(invoice.invoice_date || invoice.order_date)}</span>
              `}
            </div>
          </div>
          <div class="company-logo">
            <img src="https://app.teamther.ai/images/logo.png" alt="Teamther.ai" />
          </div>
        </div>
        
        <!-- Billing Section -->
        <div class="billing-section">
          <!-- Bill To: Organization + User Name - LEFT -->
          <div class="bill-to">
            <h3>${t.billTo}</h3>
            <div class="bill-to-content">
              ${invoice.organisation_details?.org_name ? `
                <div>
                  <div style="font-weight: 700; font-size: 15px; margin-bottom: 5px;">${invoice.organisation_details.org_name}</div>
                  ${invoice.organisation_details?.org_phone ? `<div>${invoice.organisation_details.org_phone}</div>` : ''}
                  ${invoice.organisation_details?.address ? `<div>${invoice.organisation_details.address}</div>` : ''}
                  <div>${[invoice.organisation_details?.city, invoice.organisation_details?.zipcode, invoice.organisation_details?.country].filter(Boolean).join(', ')}</div>
                  <div class="bill-to-name" style="margin-top: 8px;">${invoice.client_details?.name_complete || invoice.user_name || 'N/A'}</div>
                </div>
              ` : `
                <div class="bill-to-name">${invoice.client_details?.name_complete || invoice.user_name || 'N/A'}</div>
                ${invoice.user_email ? `<div>${invoice.user_email}</div>` : ''}
              `}
            </div>
          </div>
          
          <!-- Teamther Details Right -->
          <div class="bill-to right">
            <div class="bill-to-content">
              <div class="bill-to-name">${invoice.teamther_details?.company_name || 'Teamther.ai'}</div>
              ${invoice.teamther_details?.vat_number ? `<div>Tax #: ${invoice.teamther_details.vat_number}</div>` : ''}
              ${invoice.teamther_details?.address ? `<div>${invoice.teamther_details.address}</div>` : ''}
              ${invoice.teamther_details?.city ? `<div>${invoice.teamther_details.city}</div>` : ''}
              ${invoice.teamther_details?.zipcode ? `<div>${invoice.teamther_details.zipcode}</div>` : ''}
              ${invoice.teamther_details?.country ? `<div>${invoice.teamther_details.country}</div>` : ''}
            </div>
          </div>
        </div>
        
        <!-- Items Table -->
        <table>
          <thead>
            <tr>
              <th>${t.items}</th>
              <th style="text-align: center;">${t.priceUsd}</th>
              <th style="text-align: center;">${t.qty}</th>
              <th>${t.total}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div><strong>${invoice.package_name}</strong></div>
                <div class="item-description">${getPeriodText()}</div>
                ${invoice.package_features && invoice.package_features.length > 0 ? `
                  <div style="margin-top: 10px; font-size: 11px; color: #555; line-height: 1.8;">
                    ${invoice.package_features
                      .sort((a: any, b: any) => a.display_order - b.display_order)
                      .filter((feature: any) => {
                        // Only show boolean features if they are true
                        if (feature.value_type === 'boolean') {
                          return feature.value === true;
                        }
                        return true;
                      })
                      .map((feature: any) => {
                        const title = language === 'fr' ? feature.title_fr : feature.title_en;
                        let displayValue = '';
                        if (feature.value_type === 'boolean') {
                          // For boolean, just show the title without icon
                          return `<div style="margin-bottom: 1px;">• ${title}</div>`;
                        } else if (feature.value_type === 'integer') {
                          displayValue = feature.value.toString();
                        } else {
                          displayValue = feature.value;
                        }
                        return `<div style="margin-bottom: 1px;">• ${title}: ${displayValue}</div>`;
                      }).join('')}
                  </div>
                ` : ''}
              </td>
              <td style="text-align: center; font-size: 14px;">€${packagePrice.toFixed(2)}</td>
              <td style="text-align: center; font-size: 14px;">1</td>
              <td style="text-align: right; font-size: 14px; font-weight: 600;">€${packagePrice.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        
        <!-- Summary -->
        <div class="summary">
          ${discountAmount > 0 ? `
          <div class="summary-row" style="padding: 8px 0; font-size: 15px; color: #000;">
            <span style="font-weight: 500;">${language === 'fr' ? 'Remise' : 'Discount'}${discountPercent > 0 ? ' ' + discountPercent.toFixed(0) + '%' : ''}</span>
            <span style="font-weight: 600;">-€${discountAmount.toFixed(2)}</span>
          </div>
          ` : ''}
          <div class="summary-row" style="padding: 8px 0; font-size: 15px; color: #000;">
            <span style="font-weight: 500;">${t.subtotal}</span>
            <span style="font-weight: 600;">€${subtotal.toFixed(2)}</span>
          </div>
          ${tvaAmount > 0 ? `
          <div class="summary-row" style="padding: 8px 0; font-size: 15px; color: #000;">
            <span style="font-weight: 500;">${t.vat} ${tvaPercent}%</span>
            <span style="font-weight: 600;">€${tvaAmount.toFixed(2)}</span>
          </div>
          ` : ''}
          <div class="summary-row total" style="padding: 12px 0; font-size: 17px; border-top: 2px solid #1a1f36; margin-top: 4px;">
            <span style="font-weight: 500;">${t.total}</span>
            <span style="font-weight: 700; font-size: 18px;">€${totalToPay.toFixed(2)}</span>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="footer">
          ${invoiceFooter ? `<p>${invoiceFooter}</p>` : ''}
        </div>
      </div>
      
      <script>
        window.onload = function() {
          window.print();
        }
      </script>
    </body>
    </html>
  `;

  pdfWindow.document.write(htmlContent);
  pdfWindow.document.close();
};
